import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../context/AppContext';
import type { AppContextType } from '../types';

interface SemanticData {
    seo?: {
        title: string;
        description: string;
        keywords: string;
        canonical: string;
        robots?: string;
        ogTitle?: string;
        ogDescription?: string;
        ogImage?: string;
        twitterCard?: string;
        twitterTitle?: string;
        twitterDescription?: string;
        twitterImage?: string;
        jsonLd?: Record<string, unknown> | null;
    };
}

interface MetaTagsProps {
    semanticData?: SemanticData;
}

const upsertMeta = (key: 'name' | 'property', value: string, content: string) => {
    let element = document.head.querySelector(`meta[${key}="${value}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(key, value);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
};

const upsertCanonical = (canonicalUrl: string) => {
    let element = document.head.querySelector('link[rel="canonical"]');
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', 'canonical');
        document.head.appendChild(element);
    }
    element.setAttribute('href', canonicalUrl);
};

const upsertStructuredData = (payload: Record<string, unknown>) => {
    const id = 'seo-json-ld';
    let element = document.getElementById(id) as HTMLScriptElement | null;
    if (!element) {
        element = document.createElement('script');
        element.id = id;
        element.type = 'application/ld+json';
        document.head.appendChild(element);
    }
    element.textContent = JSON.stringify(payload);
};

const MetaTags: React.FC<MetaTagsProps> = ({ semanticData }) => {
    const context = useContext(AppContext);
    const { resolvedPageContext } = (context || {}) as Partial<AppContextType>;
    const [resolvedSemanticData, setResolvedSemanticData] = useState<SemanticData | null>(null);

    useEffect(() => {
        if (semanticData || !resolvedPageContext?.canonicalPath) {
            setResolvedSemanticData(null);
            return;
        }
        const controller = new AbortController();
        setResolvedSemanticData(null);
        void fetch(`/api/public/page-pack?path=${encodeURIComponent(resolvedPageContext.canonicalPath)}`, {
            headers: { Accept: 'application/json' },
            signal: controller.signal
        })
            .then((response) => response.ok ? response.json() as Promise<SemanticData> : Promise.reject(new Error(`PAGE_PACK_${response.status}`)))
            .then((payload) => {
                if (payload?.seo?.title && payload.seo.description) setResolvedSemanticData(payload);
            })
            .catch((error: unknown) => {
                if (!(error instanceof DOMException && error.name === 'AbortError')) setResolvedSemanticData(null);
            });
        return () => controller.abort();
    }, [semanticData, resolvedPageContext?.canonicalPath, resolvedPageContext?.pageArchetype]);

    const effectiveSemanticData = semanticData || resolvedSemanticData;

    useEffect(() => {
        const seo = effectiveSemanticData?.seo;
        if (!resolvedPageContext || !seo?.title || !seo.description) return;

        const requestedCanonical = seo.canonical || resolvedPageContext.canonicalUrl;
    const canonicalUrl = new URL(requestedCanonical, window.location.origin).pathname.replace(/\/$/, '') === window.location.pathname.replace(/\/$/, '') ? window.location.origin + window.location.pathname : requestedCanonical;
    const staticRobots = document.head.querySelector('meta[name="robots"]')?.getAttribute('content') || '';
    const robots = /^noindex/.test(seo.robots || '') && /^index/.test(staticRobots) ? staticRobots : seo.robots || staticRobots || 'index, follow';
        const image = seo.ogImage || seo.twitterImage || `${window.location.origin}/favicon.svg`;

        document.title = seo.title;
        upsertMeta('name', 'description', seo.description);
        upsertMeta('name', 'keywords', seo.keywords || '');
        upsertMeta('name', 'robots', robots);
        upsertMeta('property', 'og:title', seo.ogTitle || seo.title);
        upsertMeta('property', 'og:description', seo.ogDescription || seo.description);
        upsertMeta('property', 'og:url', canonicalUrl);
        upsertMeta('property', 'og:image', image);
        upsertMeta('name', 'twitter:card', seo.twitterCard || 'summary_large_image');
        upsertMeta('name', 'twitter:title', seo.twitterTitle || seo.ogTitle || seo.title);
        upsertMeta('name', 'twitter:description', seo.twitterDescription || seo.ogDescription || seo.description);
        upsertMeta('name', 'twitter:image', seo.twitterImage || image);
        upsertCanonical(canonicalUrl);
        if (seo.jsonLd) upsertStructuredData(seo.jsonLd);
    }, [
        resolvedPageContext,
        effectiveSemanticData
    ]);

    return null;
};

export default MetaTags;
